/**
 * Simple settings API for application config
 */
import { supabase } from '@/lib/supabaseClient';

// Type definition for payment method
export interface PaymentMethod {
  id: number;
  name: string;
  icon: string | null;
  enabled: boolean;
  sort_order: number;
}

// Type definition for app settings
export interface AppSettings {
  minOrderAmount: number;
  paymentMethodsText: string;
  paymentMethods: PaymentMethod[];
  googleAnalyticsId: string;
  orderConfirmationText: string;
}

// Default settings when nothing is configured
const DEFAULT_SETTINGS: AppSettings = {
  minOrderAmount: 0,
  paymentMethodsText: 'Följande betalningssätt accepteras:',
  paymentMethods: [
    { id: 1, name: 'Swish', icon: 'swish-logo.png', enabled: true, sort_order: 1 },
    { id: 2, name: 'Kontant', icon: 'cash-icon.png', enabled: true, sort_order: 2 }
  ],
  googleAnalyticsId: '',
  orderConfirmationText: 'Tack för din order. En bekräftelse kommer skickas till din e-post inom kort. Önskar du ändra i din order kontakta oss via diffen@me.com så hjälper vi till.'
};

// Server cache to reduce Supabase egress
const SERVER_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
interface CacheItem<T> {
  data: T;
  timestamp: number;
}

const serverCache: Record<string, CacheItem<any>> = {};

function getFromServerCache<T>(key: string): T | null {
  const item = serverCache[key];
  if (item && Date.now() - item.timestamp < SERVER_CACHE_DURATION) {
    return item.data;
  }
  return null;
}

function setInServerCache<T>(key: string, data: T): void {
  serverCache[key] = {
    data,
    timestamp: Date.now()
  };
}

export function invalidateCache(key?: string): void {
  if (key) {
    delete serverCache[key];
  } else {
    // Clear all cache
    Object.keys(serverCache).forEach(k => delete serverCache[k]);
  }
}

/**
 * Get all application settings
 * @returns AppSettings object with all settings
 */
export async function getAppSettings(): Promise<AppSettings> {
  try {
    // Check server cache first
    const cachedSettings = getFromServerCache<AppSettings>('app_settings');
    if (cachedSettings) {
      return cachedSettings;
    }

    // Try to get settings from Supabase using RPC functions
    const [
      orderConfirmationResponse,
      minOrderAmountResponse, 
      paymentMethodsResponse, 
      paymentMethodsTextResponse, 
      googleAnalyticsIdResponse
    ] = await Promise.all([
      supabase.rpc('get_setting', { p_key: 'order_confirmation_text' }),
      supabase.rpc('get_setting', { p_key: 'min_order_amount' }),
      supabase.from('payment_methods').select('id, name, icon, enabled, sort_order').order('sort_order', { ascending: true }),
      supabase.rpc('get_setting', { p_key: 'payment_methods_text' }),
      supabase.rpc('get_setting', { p_key: 'google_analytics_id' })
    ]).catch(error => {
      console.warn('Error fetching settings:', error);
      return [{ error }, { error }, { error }, { error }, { error }];
    });

    // Parse and validate the responses
    let minOrderAmount = 0;
    try {
      minOrderAmount = parseFloat(minOrderAmountResponse.data || '0');
      if (isNaN(minOrderAmount)) minOrderAmount = 0;
    } catch (e) {
      minOrderAmount = 0;
    }
    
    const paymentMethods = Array.isArray(paymentMethodsResponse.data) 
      ? paymentMethodsResponse.data 
      : DEFAULT_SETTINGS.paymentMethods;
      
    const paymentMethodsText = typeof paymentMethodsTextResponse.data === 'string'
      ? paymentMethodsTextResponse.data
      : DEFAULT_SETTINGS.paymentMethodsText;

    const googleAnalyticsId = typeof googleAnalyticsIdResponse.data === 'string'
      ? googleAnalyticsIdResponse.data
      : DEFAULT_SETTINGS.googleAnalyticsId;
      
    const orderConfirmationText = typeof orderConfirmationResponse.data === 'string'
      ? orderConfirmationResponse.data
      : DEFAULT_SETTINGS.orderConfirmationText;

    const settings = {
      minOrderAmount,
      paymentMethods,
      paymentMethodsText,
      googleAnalyticsId,
      orderConfirmationText
    };

    // Store in server cache
    setInServerCache('app_settings', settings);

    return settings;
  } catch (error) {
    console.error('Error in getAppSettings:', error);
    return { ...DEFAULT_SETTINGS };
  }
}

/**
 * Set minimum order amount
 * @param amount Minimum order amount in SEK
 * @returns true if successful
 */
export async function setMinOrderAmount(amount: number): Promise<boolean> {
  try {
    // Validate amount
    if (amount < 0) {
      return false;
    }
    
    // Update using RPC function
    const { error } = await supabase.rpc('set_setting', { 
      p_key: 'min_order_amount', 
      p_value: amount.toString(),
      p_description: 'Minimum order amount in SEK'
    });
    
    if (error) {
      console.error('Error in setMinOrderAmount:', error);
      return false;
    }
    
    // Invalidate cache
    invalidateCache('app_settings');
    return true;
  } catch (error) {
    console.error('Error in setMinOrderAmount:', error);
    return false;
  }
}

/**
 * Update payment methods text
 * @param text The text to display above payment methods
 * @returns true if successful
 */
export async function setPaymentMethodsText(text: string): Promise<boolean> {
  try {
    const { error } = await supabase.rpc('set_setting', { 
      p_key: 'payment_methods_text', 
      p_value: text,
      p_description: 'Text displayed above payment methods'
    });
    
    if (error) {
      console.error('Error in setPaymentMethodsText:', error);
      return false;
    }
    
    // Invalidate cache
    invalidateCache('app_settings');
    return true;
  } catch (error) {
    console.error('Error in setPaymentMethodsText:', error);
    return false;
  }
}

/**
 * Update Google Analytics ID
 * @param id Google Analytics ID (G-XXXXXXXXXX)
 * @returns true if successful
 */
export async function setGoogleAnalyticsId(id: string): Promise<boolean> {
  try {
    const { error } = await supabase.rpc('set_setting', { 
      p_key: 'google_analytics_id', 
      p_value: id,
      p_description: 'Google Analytics 4 Measurement ID'
    });
    
    if (error) {
      console.error('Error in setGoogleAnalyticsId:', error);
      return false;
    }
    
    // Invalidate cache
    invalidateCache('app_settings');
    return true;
  } catch (error) {
    console.error('Error in setGoogleAnalyticsId:', error);
    return false;
  }
}

/**
 * Save a payment method
 * @param method Payment method to save
 * @returns true if successful
 */
export async function savePaymentMethod(method: Partial<PaymentMethod> & { id?: number }): Promise<boolean> {
  try {
    if (!method.name) {
      return false;
    }
    
    if (method.id) {
      // Update existing method
      const { error } = await supabase
        .from('payment_methods')
        .update({
          name: method.name,
          icon: method.icon,
          enabled: method.enabled,
          sort_order: method.sort_order
        })
        .eq('id', method.id);
        
      if (error) {
        console.error('Error updating payment method:', error);
        return false;
      }
    } else {
      // Create new method
      const { error } = await supabase
        .from('payment_methods')
        .insert({
          name: method.name,
          icon: method.icon,
          enabled: method.enabled,
          sort_order: method.sort_order
        });
        
      if (error) {
        console.error('Error creating payment method:', error);
        return false;
      }
    }
    
    // Invalidate cache
    invalidateCache('app_settings');
    return true;
  } catch (error) {
    console.error('Error in savePaymentMethod:', error);
    return false;
  }
}

/**
 * Delete a payment method
 * @param id Payment method ID to delete
 * @returns true if successful
 */
export async function deletePaymentMethod(id: number): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('payment_methods')
      .delete()
      .eq('id', id);
      
    if (error) {
      console.error('Error deleting payment method:', error);
      return false;
    }
    
    // Invalidate cache
    invalidateCache('app_settings');
    return true;
  } catch (error) {
    console.error('Error in deletePaymentMethod:', error);
    return false;
  }
} 